import { Slot } from "radix-ui";
import { LoaderCircle } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { cn } from "../lib/cn";

export type ButtonVariant = "primary" | "secondary" | "soft" | "ghost" | "danger" | "danger-ghost";
export type ButtonSize = "sm" | "md" | "lg";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-primary text-on-primary shadow-card hover:bg-primary-hover active:bg-primary-active",
  secondary: "border border-border-strong bg-surface text-fg shadow-card hover:bg-surface-hover active:bg-surface-active",
  soft: "bg-primary-soft text-primary-text hover:bg-primary-soft-hover",
  ghost: "text-fg-muted hover:bg-surface-hover hover:text-fg active:bg-surface-active",
  danger: "bg-danger-solid text-on-danger shadow-card hover:bg-danger-solid-hover",
  "danger-ghost": "text-danger hover:bg-danger-soft",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-11 px-3 text-sm md:h-8",
  md: "h-11 px-4 text-[0.9375rem] md:h-10 md:text-sm",
  lg: "h-12 px-6 text-base",
};

export function buttonClass(variant: ButtonVariant = "primary", size: ButtonSize = "md", className?: string) {
  return cn(
    "inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors duration-150",
    "disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-[1.125rem] [&_svg]:shrink-0",
    VARIANTS[variant],
    SIZES[size],
    className,
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Renders the child element, such as a link, with the button's look. */
  asChild?: boolean;
  icon?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({ variant, size, loading, asChild, icon, className, children, disabled, type, ...rest }: ButtonProps) {
  if (asChild) {
    return (
      <Slot.Root className={buttonClass(variant, size, className)} {...rest}>
        {icon}
        <Slot.Slottable>{children}</Slot.Slottable>
      </Slot.Root>
    );
  }
  return (
    <button type={type ?? "button"} disabled={disabled || loading} aria-busy={loading || undefined} className={buttonClass(variant, size, className)} {...rest}>
      {loading ? <LoaderCircle className="animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
}
